import { ColDef } from "@ag-grid-community/core";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getClosingStatsRetrieveQueryKey,
  useClosingSecurityOwnershipBulkCreate,
} from "gen/endpoints/closing/closing";
import { getCommitmentInformationTableDataRetrieveQueryKey } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useInvestorClassListSuspense } from "gen/endpoints/investor-class/investor-class";
import { useInvestorEntityListSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { ClosingCommitmentCreateData } from "gen/model/closingCommitmentCreateData";
import { ClosingData } from "gen/model/closingData";
import { get } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavConfirmationConfirmDialog } from "shared/components/form/FormNavConfirmationDialog";
import {
  ConfirmationDialogProps,
  useConfirmationDialog,
} from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { useMaybernTableBulkEditController } from "shared/components/table/maybern/MaybernTableBulkEditController";
import { useToast } from "shared/components/toast/useToast";
import { ModalCancelButton } from "shared/modal/ModalCancelButton";
import {
  flattenApiErrorData,
  getApiError,
  getApiErrorFromResponse,
} from "shared/utils/ErrorUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface CommitmentBulkAddModalProps {
  fundFamilyId: string;
  closing: ClosingData;
}

export const CommitmentBulkAddModal: React.FC<
  ConfirmationDialogProps<void, CommitmentBulkAddModalProps>
> = ({
  alertProps: { onClose, ...alertProps },
  extraProps: { fundFamilyId, closing },
}) => {
  const { t } = useTranslation(["commitment", "common"]);
  const { showToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { data: investors } = useInvestorEntityListSuspense();
  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });
  const { data: investorClasses } = useInvestorClassListSuspense(fundFamilyId);

  const {
    mutateAsync: createCommitments,
    variables: createCommitmentsVariables,
    error: createCommitmentsError,
    reset,
    isSuccess,
  } = useClosingSecurityOwnershipBulkCreate();

  const {
    gridRef,
    datasource,
    onClickClear,
    onCellValueChanged,
    getErrorForCell,
    rowData,
  } = useMaybernTableBulkEditController<ClosingCommitmentCreateData>({
    getSubmittedFieldValue: useCallback(
      (rowIndex, field) =>
        get(
          createCommitmentsVariables,
          `data.closing_commitments.${rowIndex}.${field}`,
        ),
      [createCommitmentsVariables],
    ),
    getFieldError: useCallback(
      (rowIndex, field) =>
        get(
          getApiErrorFromResponse(createCommitmentsError)?.data,
          `closing_commitments.${rowIndex}.${field}`,
        ),
      [createCommitmentsError],
    ),
  });

  const isDirty = rowData.length > 0 && !isSuccess;

  const confirmCancel = useConfirmationDialog(NavConfirmationConfirmDialog);

  const columnDefs = useMemo<ColDef<ClosingCommitmentCreateData>[]>(
    () => [
      {
        field: "owning_entity_id",
        headerName: t("commitment:bulkAddModal.columns.investorEntity.title"),
        type: ["dropdownEditable"],
        initialWidth: 220,
        cellEditorParams: {
          title: t(
            "commitment:bulkAddModal.columns.investorEntity.editor.title",
          ),
          options: investors.map((i) => ({
            label: i.legal_name,
            value: i.id,
          })),
        },
      },
      {
        field: "commitment_amount",
        headerName: t("commitment:bulkAddModal.columns.commitmentAmount.title"),
        type: ["moneyEditable"],
        editable: true,
        initialWidth: 200,
      },
      {
        field: "owned_entity_id",
        headerName: t("commitment:bulkAddModal.columns.fund.title"),
        type: ["dropdownEditable"],
        initialWidth: 200,
        cellEditorParams: {
          title: t("commitment:bulkAddModal.columns.fund.editor.title"),
          options: funds.map((f) => ({
            label: f.legal_name,
            value: f.id,
          })),
        },
      },
      {
        field: "investor_class_id",
        headerName: t("commitment:bulkAddModal.columns.investorClass.title"),
        type: ["dropdownEditable"],
        initialWidth: 200,
        cellEditorParams: {
          title: t(
            "commitment:bulkAddModal.columns.investorClass.editor.title",
          ),
          options: investorClasses.map((i) => ({
            label: i.name,
            value: i.id,
          })),
        },
      },
    ],
    [funds, investorClasses, investors, t],
  );

  const onSubmit = useCallback(async () => {
    try {
      const resp = await createCommitments({
        fundFamilyId,
        closingId: closing.id!,
        data: {
          closing_commitments: rowData,
        },
        params: {
          version_date: closing.closing_date,
        },
      });
      await invalidateQueryKeys(
        queryClient,
        getCommitmentInformationTableDataRetrieveQueryKey({
          fund_family_id: fundFamilyId,
          closing_id: closing.id,
        }),
        getCommitmentInformationTableDataRetrieveQueryKey({
          fund_family_id: fundFamilyId,
          is_unattached_to_closing: true,
        }),
        getClosingStatsRetrieveQueryKey(fundFamilyId, closing.id!),
      );
      showToast({
        variant: "success",
        message: t("commitment:bulkAddModal.success", {
          count: resp.security_ownerships.length,
        }),
      });
      onClose();
    } catch (e) {
      const rootListError = flattenApiErrorData(getApiError(e)?.data).find(
        (error) => error.fieldPath === "closing_commitments",
      );
      if (rootListError) {
        showErrorToast(rootListError.error);
      }
    }
  }, [
    queryClient,
    closing.closing_date,
    closing.id,
    createCommitments,
    fundFamilyId,
    onClose,
    rowData,
    showErrorToast,
    showToast,
    t,
  ]);

  const onModalClose = useCallback(async () => {
    if (isDirty) {
      const confirmResult = await confirmCancel();
      if (confirmResult === "submit") {
        await onSubmit();
      } else if (confirmResult === "discard") {
        onClose();
      }
    } else {
      onClose();
    }
  }, [confirmCancel, isDirty, onClose, onSubmit]);

  return (
    <Modal
      size="full"
      scrollBehavior="inside"
      onClose={onModalClose}
      {...alertProps}
    >
      <ModalContent>
        <ModalBody px="0" display="flex" flexDirection="column">
          <TableBox isBulkEditor={true}>
            <TableBoxHeader bg="eggplant.25">
              <TableBoxTitle>
                {t("commitment:bulkAddModal.title")}
              </TableBoxTitle>
              <TableBoxActions showFullScreen={false}>
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={() => {
                    onClickClear();
                    reset();
                  }}
                >
                  {t("common:clear")}
                </Button>
              </TableBoxActions>
            </TableBoxHeader>
            <TableBoxBody display="flex" minHeight="0">
              <MaybernTable
                ref={gridRef}
                onCellValueChanged={onCellValueChanged}
                columnDefs={columnDefs}
                defaultColDef={{
                  sortable: false,
                }}
                getErrorForCell={getErrorForCell}
                enableRangeSelection={true}
                enableFillHandle={true}
                rowModelType="serverSide"
                serverSideDatasource={datasource}
                maxConcurrentDatasourceRequests={1}
              />
            </TableBoxBody>
          </TableBox>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" gap="8px" py="12px" px="24px">
          <ModalCancelButton />
          <Button onClick={onSubmit}>
            {t("commitment:bulkAddModal.submit")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
